import { AdjustableInterpolatorWrapper } from 'app/shared/collections/time/interpolator/interpolator';
import { toUnix } from 'app/shared/utilities/time';
import {
    closestPastSample,
    Interpolator,
    TimeSeriesCollectionInterface,
} from 'time-series-collection';

/**
 * Creates an interpolator which picks the data point
 *  which falls in the range of the given timestamp.
 *
 *  Overlapping ranges are NOT supported, you'll get the result which starts latest, which covers the timestamp specified
 * @returns {Interpolator<T>} An interpolator to use when getting values from a time series collection
 */
export function getDateWithinTimeRangeInterpolator<
    T extends { end_timestamp: number },
>(): Interpolator<T> {
    return (
        collection: TimeSeriesCollectionInterface<T>,
        targetTimestamp: number,
        targetIndex: number,
    ) => {
        if (!collection.datums[targetIndex - 1]?.end_timestamp) {
            return undefined;
        }
        const endTimestamp = toUnix(new Date(collection.datums[targetIndex - 1].end_timestamp));
        return targetTimestamp >= collection.timestamps[targetIndex - 1] &&
            targetTimestamp <= endTimestamp
            ? collection.datums[targetIndex - 1]
            : undefined;
    };
}
// this object allows the use of the closestPastSample from the 'time-series-collection' library
// and exposes a mechanism to get and set the statHoldSeconds for that interpolator on the fly
export class AdjustableClosestPastSampleInterpolator implements AdjustableInterpolatorWrapper {
    _statHoldSeconds: number;
    _interpolatorFactory: CallableFunction;
    _interpolator: Interpolator<unknown>;

    constructor(
        statHoldSeconds: number,
        // default normally used; overridden for testing
        interpolatorFactory: (number) => Interpolator<unknown> = closestPastSample,
    ) {
        this._interpolatorFactory = interpolatorFactory;
        this.setStatHoldSeconds(statHoldSeconds);
    }

    public getStatHoldSeconds(): number {
        return this._statHoldSeconds;
    }

    public setStatHoldSeconds(statHoldSeconds: number): void {
        if (statHoldSeconds === this._statHoldSeconds) {
            return;
        }

        this._statHoldSeconds = statHoldSeconds;
        this._interpolator = this._interpolatorFactory(this._statHoldSeconds);
    }

    // this is the actual interpolator method given to a Processor- calling this method should feel
    // the same as calling closestPastSample
    get interpolator(): Interpolator<any> {
        return (
            collection: TimeSeriesCollectionInterface<unknown>,
            targetTimestamp: number,
            targetIndex: number,
        ) => {
            return this._interpolator(collection, targetTimestamp, targetIndex);
        };
    }
}
